/**
 * @voltxampere/engine — Switch DC stamp (Sprint 22 B1)
 *
 * A switch is a state-less linear conductance. The `closed`
 * boolean parameter selects between the on-resistance `Ron` (when
 * closed → conducting) and the off-resistance `Roff` (when open →
 * near-isolated). The conductance is written to the same four MNA
 * cells a resistor would, so either pin may be ground without a
 * special branch.
 *
 *   closed: false (default) → G = 1/Roff (Roff defaults to 1 GΩ)
 *   closed: true            → G = 1/Ron  (Ron  defaults to 1 mΩ)
 *
 * Sprint 22 plan (D-22-2): Roff defaults to a value at the Gmin
 * scale so a stand-alone open switch is naturally well-conditioned
 * by the existing Gmin-stepping fallback path.
 *
 * The transient + AC stamps reuse this DC body verbatim (the
 * conductance has no frequency or time dependence); only a thin
 * complex-matrix wrapper lives next to the AC stamps file.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
import { defineDCStamp } from "../definition.js";
import { getTerminalNetId } from "../terminal-utils.js";
const DEFAULT_RON = 1e-3;
const DEFAULT_ROFF = 1e9;
/**
 * Read the switch's parameters with the same defensive defaults
 * the Zod schema applies. Engine code paths must never assume
 * the parameter pre-validator already ran (legacy circuits, raw
 * test fixtures) — this helper centralises the fallback.
 */
export function resolveSwitchParams(componentId, params) {
    const closedRaw = params.closed;
    const closed = typeof closedRaw === "boolean" ? closedRaw : false;
    const ronRaw = params.Ron;
    const ron = typeof ronRaw === "number" && ronRaw > 0 && Number.isFinite(ronRaw) ? ronRaw : DEFAULT_RON;
    const roffRaw = params.Roff;
    const roff = typeof roffRaw === "number" && roffRaw > 0 && Number.isFinite(roffRaw) ? roffRaw : DEFAULT_ROFF;
    if (ron >= roff) {
        throw new ContractViolationError(`switch "${componentId}": Ron (${String(ron)}) must be strictly less than Roff (${String(roff)})`);
    }
    return { closed, Ron: ron, Roff: roff };
}
export const switchDCStamp = (ctx, component) => {
    const { closed, Ron, Roff } = resolveSwitchParams(component.id, component.params);
    const g = 1 / (closed ? Ron : Roff);
    const a = ctx.nodeIndex(getTerminalNetId(component, "t1"));
    const b = ctx.nodeIndex(getTerminalNetId(component, "t2"));
    ctx.addY(a, a, g);
    ctx.addY(b, b, g);
    ctx.addY(a, b, -g);
    ctx.addY(b, a, -g);
};
/** Pure conductance — no augmented MNA rows. */
export const switchDCStampDefinition = defineDCStamp("switch", switchDCStamp, []);
//# sourceMappingURL=switch.js.map